/**
 * @module index
 * @package @geenius/adapters/solidjs
 * @description Implements the SolidJS adapter provider and context access
 * helpers. This module owns adapter status initialization for the SolidJS
 * runtime package while keeping the shared package framework-agnostic.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  AdapterContextError,
  AdapterError,
  createConsoleLoggerAdapter,
  createLocalStorageAdminAdapter,
  createLocalStorageAiAdapter,
  createLocalStorageAuthAdapter,
  createLocalStorageDbAdapter,
  createLocalStorageFileAdapter,
  createLocalStoragePaymentsAdapter,
  createNoopPaymentsAdapter,
  DOMAIN_LABELS,
  getDomainConfig,
  isAdaptersConfigured,
} from "@geenius/adapters";
import type { Context, JSX } from "solid-js";
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";

/**
 * Complete adapter implementation map accepted by the SolidJS provider.
 *
 * @property db Database adapter implementation.
 * @property auth Authentication adapter implementation.
 * @property payments Payments adapter implementation.
 * @property ai AI adapter implementation.
 * @property storage File-storage adapter implementation.
 * @property admin Admin adapter implementation.
 * @property logger Logger adapter implementation.
 */
export interface AdapterDomainMap {
  db: DbAdapter;
  auth: AuthAdapter;
  payments: PaymentsAdapter;
  ai: AiAdapter;
  storage: FileStorageAdapter;
  admin: AdminAdapter;
  logger: LoggerAdapter;
}

/**
 * Public SolidJS provider domain key alias derived from the adapter map.
 *
 * Use this when a consumer needs a key constrained to configured provider slots
 * rather than the wider shared adapter-domain vocabulary.
 */
export type AdapterDomainKey = keyof AdapterDomainMap;

/**
 * SolidJS adapter bag keyed by domain.
 *
 * @property db Database adapter implementation when configured.
 * @property auth Authentication adapter implementation when configured.
 * @property payments Payments adapter implementation when configured.
 * @property ai AI adapter implementation when configured.
 * @property storage File-storage adapter implementation when configured.
 * @property fileStorage Alias for the file-storage adapter accepted by the slot API.
 * @property admin Admin adapter implementation when configured.
 * @property logger Logger adapter implementation when configured.
 */
export interface AdapterSet extends Partial<AdapterDomainMap> {
  fileStorage?: FileStorageAdapter;
}

/** Registry shape accepted by the public SolidJS provider. */
export type AdapterRegistry = AdapterSet;

/**
 * SolidJS adapter context value exposed to primitives and provider consumers.
 *
 * @property adapters Adapter bag passed into the provider.
 * @property statuses Current status record for each adapter domain.
 * @property getAdapter Returns a configured adapter for a specific domain or throws when missing.
 * @property isReady Reports whether a domain is configured and connected.
 * @property isLoading Indicates whether initial health checks are still running.
 */
export interface AdapterContextValue {
  adapters: AdapterSet;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  getAdapter: <K extends AdapterDomainKey>(domain: K) => AdapterDomainMap[K];
  isReady: (domain: AdapterDomain) => boolean;
  isLoading: boolean;
}

const AdapterContext: Context<AdapterContextValue | undefined> =
  createContext<AdapterContextValue>();
type ProviderChildren = Parameters<
  typeof AdapterContext.Provider
>[0]["children"];

/**
 * Map of domain-specific health probes supplied by the host app.
 *
 * When a domain has a custom probe, the provider calls it instead of the
 * built-in probe during the health-check lifecycle. The probe receives the
 * adapter instance for its domain and the full adapter bag.
 */
export type AdapterHealthProbeMap = Partial<{
  [D in AdapterDomainKey]: (
    adapter: AdapterDomainMap[D],
    adapters: AdapterSet,
  ) => Promise<void> | void;
}>;

/**
 * Props accepted by the SolidJS adapter provider.
 *
 * @property adapters Adapter bag made available to descendant primitives and components.
 * @property registry Optional registry alias for adapter overrides.
 * @property db Optional direct database adapter override.
 * @property auth Optional direct authentication adapter override.
 * @property payments Optional direct payments adapter override.
 * @property ai Optional direct AI adapter override.
 * @property storage Optional direct file-storage adapter override.
 * @property fileStorage Optional slot-API alias for the file-storage adapter.
 * @property admin Optional direct admin adapter override.
 * @property logger Optional direct logger adapter override.
 * @property children Solid subtree that should receive adapter context.
 * @property healthCheck Whether the provider should probe configured adapters on mount.
 * @property healthProbes Optional domain-specific health probes supplied by the host app.
 * @property statusOverrides Optional deterministic status overrides for tests and previews.
 * @property onError Optional typed error callback invoked when provider lifecycle checks fail.
 */
export interface AdapterProviderProps extends Partial<AdapterDomainMap> {
  adapters?: AdapterSet;
  registry?: Partial<AdapterRegistry>;
  fileStorage?: FileStorageAdapter;
  children: ProviderChildren;
  healthCheck?: boolean;
  healthProbes?: AdapterHealthProbeMap;
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>;
  onError?: (error: AdapterError) => void;
}

/**
 * Public compatibility alias for the SolidJS adapter provider props.
 *
 * The plural name mirrors the public `AdaptersProvider` component alias while
 * preserving the exact same prop contract as `AdapterProviderProps`.
 */
export type AdaptersProviderProps = AdapterProviderProps;

interface ResolvedAdapterSet {
  adapters: AdapterSet;
  fallbackDomains: ReadonlySet<AdapterDomainKey>;
}

const ADAPTER_DOMAIN_KEYS = ADAPTER_DOMAINS as readonly AdapterDomainKey[];

const DEFAULT_PROVIDER_BY_DOMAIN: Record<AdapterDomainKey, string> = {
  db: "localStorage",
  auth: "localStorage",
  payments: "localStorage",
  ai: "localStorage",
  storage: "localStorage",
  admin: "localStorage",
  logger: "console",
};

function createDefaultPaymentsAdapter(): PaymentsAdapter {
  try {
    return createLocalStoragePaymentsAdapter();
  } catch {
    return createNoopPaymentsAdapter();
  }
}

function createDefaultAdapter(
  domain: AdapterDomainKey,
): AdapterDomainMap[AdapterDomainKey] {
  switch (domain) {
    case "db":
      return createLocalStorageDbAdapter();
    case "auth":
      return createLocalStorageAuthAdapter();
    case "payments":
      return createDefaultPaymentsAdapter();
    case "ai":
      return createLocalStorageAiAdapter();
    case "storage":
      return createLocalStorageFileAdapter();
    case "admin":
      return createLocalStorageAdminAdapter();
    case "logger":
      return createConsoleLoggerAdapter();
  }
}

function setResolvedAdapter(
  adapters: AdapterSet,
  fallbackDomains: Set<AdapterDomainKey>,
  domain: AdapterDomainKey,
  adapter: AdapterDomainMap[AdapterDomainKey] | undefined,
): void {
  if (adapter === undefined) {
    return;
  }

  const writableAdapters = adapters as Partial<
    Record<AdapterDomainKey, AdapterDomainMap[AdapterDomainKey]>
  >;
  writableAdapters[domain] = adapter;
  fallbackDomains.delete(domain);
}

function setDefaultAdapter(
  adapters: AdapterSet,
  domain: AdapterDomainKey,
): void {
  if (adapters[domain] !== undefined) {
    return;
  }

  const writableAdapters = adapters as Partial<
    Record<AdapterDomainKey, AdapterDomainMap[AdapterDomainKey]>
  >;
  writableAdapters[domain] = createDefaultAdapter(domain);
}

function resolveAdapterSet(props: AdapterProviderProps): ResolvedAdapterSet {
  const fallbackDomains = new Set<AdapterDomainKey>(ADAPTER_DOMAIN_KEYS);
  const provided: AdapterSet = {
    ...(props.registry ?? {}),
    ...(props.adapters ?? {}),
  };
  const adapters: AdapterSet = {};

  for (const domain of ADAPTER_DOMAIN_KEYS) {
    setResolvedAdapter(adapters, fallbackDomains, domain, provided[domain]);
  }

  setResolvedAdapter(
    adapters,
    fallbackDomains,
    "storage",
    provided.fileStorage,
  );
  setResolvedAdapter(adapters, fallbackDomains, "db", props.db);
  setResolvedAdapter(adapters, fallbackDomains, "auth", props.auth);
  setResolvedAdapter(adapters, fallbackDomains, "payments", props.payments);
  setResolvedAdapter(adapters, fallbackDomains, "ai", props.ai);
  setResolvedAdapter(adapters, fallbackDomains, "storage", props.storage);
  setResolvedAdapter(adapters, fallbackDomains, "storage", props.fileStorage);
  setResolvedAdapter(adapters, fallbackDomains, "admin", props.admin);
  setResolvedAdapter(adapters, fallbackDomains, "logger", props.logger);

  for (const domain of ADAPTER_DOMAIN_KEYS) {
    setDefaultAdapter(adapters, domain);
  }

  adapters.fileStorage = adapters.storage;
  return { adapters, fallbackDomains };
}

function getInitialProviderName(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  fallbackDomains: ReadonlySet<AdapterDomainKey>,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): string {
  const overrideProvider = statusOverrides?.[domain]?.provider;
  if (overrideProvider !== undefined) {
    return overrideProvider;
  }

  if (!adapters[domain]) {
    return "none";
  }

  if (fallbackDomains.has(domain)) {
    return DEFAULT_PROVIDER_BY_DOMAIN[domain];
  }

  if (!isAdaptersConfigured()) {
    return "configured";
  }

  return getDomainConfig(domain)?.provider ?? "configured";
}

function createInitialStatus(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  fallbackDomains: ReadonlySet<AdapterDomainKey>,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): AdapterStatusInfo {
  const adapter = adapters[domain];
  return {
    domain,
    provider: getInitialProviderName(
      domain,
      adapters,
      fallbackDomains,
      statusOverrides,
    ),
    status: adapter
      ? healthCheck
        ? "initializing"
        : "connected"
      : "disconnected",
    lastCheckedAt: Date.now(),
    ...statusOverrides?.[domain],
  };
}

function createInitialStatuses(
  resolved: ResolvedAdapterSet,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): Record<AdapterDomain, AdapterStatusInfo> {
  return {
    db: createInitialStatus(
      "db",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    auth: createInitialStatus(
      "auth",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    payments: createInitialStatus(
      "payments",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    ai: createInitialStatus(
      "ai",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    storage: createInitialStatus(
      "storage",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    admin: createInitialStatus(
      "admin",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
    logger: createInitialStatus(
      "logger",
      resolved.adapters,
      resolved.fallbackDomains,
      healthCheck,
      statusOverrides,
    ),
  };
}

function toAdapterError(domain: AdapterDomain, error: unknown): AdapterError {
  if (error instanceof AdapterError) {
    return error;
  }

  const message =
    error instanceof Error ? error.message : "Health check failed";
  return new AdapterError(message, domain, "SERVICE_ERROR", error);
}

async function probeAdapterDomain(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  healthProbes?: AdapterHealthProbeMap,
): Promise<{ connected: true } | { connected: false; error: string }> {
  const customProbe = healthProbes?.[domain];
  if (customProbe) {
    await customProbe(adapters[domain] as never, adapters);
    return { connected: true };
  }

  switch (domain) {
    case "db":
      await adapters.db?.count("__geenius_adapter_healthcheck");
      return adapters.db
        ? { connected: true }
        : { connected: false, error: "DB adapter is not configured." };
    case "auth":
      await adapters.auth?.getSession();
      return adapters.auth
        ? { connected: true }
        : { connected: false, error: "Auth adapter is not configured." };
    case "payments":
      await adapters.payments?.getPlans();
      return adapters.payments
        ? { connected: true }
        : { connected: false, error: "Payments adapter is not configured." };
    case "storage":
      await adapters.storage?.list("__geenius_adapter_healthcheck");
      return adapters.storage
        ? { connected: true }
        : { connected: false, error: "Storage adapter is not configured." };
    case "admin":
      await adapters.admin?.getMetrics();
      return adapters.admin
        ? { connected: true }
        : { connected: false, error: "Admin adapter is not configured." };
    case "logger":
      await adapters.logger?.child({ healthCheck: true });
      return adapters.logger
        ? { connected: true }
        : { connected: false, error: "Logger adapter is not configured." };
    case "ai":
      await adapters.ai?.complete("__geenius_adapter_healthcheck", {
        maxTokens: 1,
        temperature: 0,
      });
      return adapters.ai
        ? { connected: true }
        : { connected: false, error: "AI adapter is not configured." };
  }
}

/**
 * Provides the SolidJS adapter context and optional health-check lifecycle.
 *
 * @param props Provider props including the adapter bag, child tree, and optional health-check settings.
 * @returns A SolidJS context provider that exposes adapters and derived status metadata.
 */
// @doc
export function AdapterProvider(props: AdapterProviderProps): JSX.Element {
  const resolved = createMemo(() => resolveAdapterSet(props));
  const adapters = createMemo(() => resolved().adapters);
  const healthCheckEnabled = createMemo(() => props.healthCheck ?? false);
  const buildInitialStatuses = (): Record<AdapterDomain, AdapterStatusInfo> =>
    createInitialStatuses(
      resolved(),
      healthCheckEnabled(),
      props.statusOverrides,
    );

  const [statuses, setStatuses] = createStore(buildInitialStatuses());
  const [isLoading, setIsLoading] = createSignal(healthCheckEnabled());

  createEffect(() => {
    const nextStatuses = buildInitialStatuses();
    setStatuses(nextStatuses);
    setIsLoading(healthCheckEnabled());
  });

  // Reactive probe loop — re-runs whenever the relevant props change so
  // statusOverrides / healthCheck / healthProbes / adapter swaps are
  // honored after the initial mount, matching the React reference's
  // useEffect-driven recomputation.
  createEffect(() => {
    // Tracked dependencies (accessed inside the reactive scope):
    const isEnabled = healthCheckEnabled();
    const currentAdapters = adapters();
    const healthProbes = props.healthProbes;
    if (!isEnabled) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    onCleanup(() => {
      cancelled = true;
    });
    void (async () => {
      const checks = ADAPTER_DOMAIN_KEYS.map(async (domain) => {
        const adapter = currentAdapters[domain];
        if (!adapter) {
          return;
        }

        try {
          if (cancelled) {
            return;
          }

          setStatuses(domain, "status", "initializing");

          const result = await probeAdapterDomain(
            domain,
            currentAdapters,
            healthProbes,
          );
          if (cancelled) {
            return;
          }

          if (!result.connected) {
            setStatuses(domain, "status", "disconnected");
            setStatuses(domain, "error", result.error);
            setStatuses(domain, "lastCheckedAt", Date.now());
            return;
          }

          setStatuses(domain, "status", "connected");
          setStatuses(domain, "error", undefined);
          setStatuses(domain, "lastCheckedAt", Date.now());
        } catch (error) {
          const adapterError = toAdapterError(domain, error);
          props.onError?.(adapterError);

          if (cancelled) {
            return;
          }

          setStatuses(domain, "status", "error");
          setStatuses(domain, "error", adapterError.message);
          setStatuses(domain, "lastCheckedAt", Date.now());
        }
      });

      await Promise.allSettled(checks);
      if (!cancelled) {
        setIsLoading(false);
      }
    })();
  });

  const getAdapter = <K extends AdapterDomainKey>(
    domain: K,
  ): AdapterDomainMap[K] => {
    const adapter = adapters()[domain] as AdapterDomainMap[K] | undefined;
    if (adapter === undefined) {
      throw new AdapterContextError(
        `${DOMAIN_LABELS[domain]} adapter not configured in <AdapterProvider>`,
        "ADAPTER_NOT_CONFIGURED",
      );
    }

    return adapter;
  };

  const isReady = (domain: AdapterDomain): boolean => {
    const domainKey = domain as AdapterDomainKey;
    return !!adapters()[domainKey] && statuses[domain]?.status === "connected";
  };

  const value: AdapterContextValue = {
    get adapters() {
      return adapters();
    },
    get statuses() {
      return statuses;
    },
    getAdapter,
    isReady,
    get isLoading() {
      return isLoading();
    },
  };

  return (
    <AdapterContext.Provider value={value}>
      {props.children}
    </AdapterContext.Provider>
  );
}

/**
 * Public compatibility alias matching the plural provider naming convention.
 *
 * Consumers can use either `AdapterProvider` or `AdaptersProvider`; both mount
 * the same SolidJS context and accept `AdapterProviderProps`.
 */
export const AdaptersProvider = AdapterProvider;

/**
 * Reads the SolidJS adapter context from the nearest provider.
 *
 * @returns The active adapter context value with adapters, statuses, and readiness helpers.
 * @throws {AdapterContextError} When called outside of an `<AdapterProvider>` tree.
 */
// @doc
export function useAdapterContext(): AdapterContextValue {
  const context = useContext(AdapterContext);
  if (!context) {
    throw new AdapterContextError(
      "useAdapterContext must be used within <AdapterProvider>",
      "MISSING_CONTEXT",
    );
  }

  return context;
}
